<script setup lang="ts">
import MissingInfoImage from "@/assets/pngs/MissingInfo.png?w=300&h=358&format=webp&imagetools"
import PreviewTable from "@/components/FileImport/PreviewTable.vue"
import Loading from "@/components/Loading.vue"
import PageBanner, { PageBannerType } from "@/components/PageBanner.vue"
import { useFileImportStore } from "@/stores"
import { Decorator, PhaseSteps } from "@/types"

import { ExclamationMarkIcon } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { storeToRefs } from "pinia"
import { computed } from "vue"

interface Emits {
  (event: "disableImport", disableImport: boolean): void
}

const emit = defineEmits<Emits>()

const fileImportStore = useFileImportStore()

const { allItemsMatched } = storeToRefs(fileImportStore)

const stepsCompleted = computed(() => {
  return fileImportStore.steps.every((step) => {
    return (
      step.title === PhaseSteps.PREVIEW || step.decorator() === Decorator.VALID
    )
  })
})

function handleOpenFileDialog() {
  fileImportStore.openFileFullDialog = true
}
</script>

<template>
  <div class="flex w-full flex-1 flex-col overflow-auto">
    <PageBanner
      v-if="!allItemsMatched"
      :type="PageBannerType.ERROR"
      label="Oops"
      title="You have pending items at the match step"
      class="pb-6 pr-80"
      :closeable="false"
    >
      <p>Please match all items in order to proceed with the import.</p>
    </PageBanner>

    <div v-if="stepsCompleted">
      <div class="flex items-center gap-2 pb-4">
        <h2 class="text-sm font-bold">
          Selected and matched data
        </h2>
        <KButton
          variety="tertiary"
          size="small"
          class="shadow-sm!"
          @click="handleOpenFileDialog"
        >
          See full file
        </KButton>
      </div>
      <Suspense>
        <PreviewTable @disable-import="emit('disableImport', $event)" />

        <template #fallback>
          <Loading class="h-40 w-40 m-auto" />
        </template>
      </Suspense>
    </div>

    <div
      v-else
      class="my-2 flex min-h-96 items-center justify-center rounded-md bg-gray-100"
    >
      <div class="flex flex-col items-center p-4 md:flex-row">
        <div>
          <img
            :src="MissingInfoImage"
            class="w-52 px-8"
            alt=""
          >
        </div>
        <div class="space-y-4 md:w-80">
          <h2 class="text-lg font-bold">
            Nothing to preview yet
          </h2>
          <p>
            Please provide the required information indicated with
            <ExclamationMarkIcon
              class="ml-2 inline h-4 w-4 text-red-400"
            />
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
